import { Link } from "react-router-dom";
import { Download } from "lucide-react";
import { DJ_BIO_SHORT, DJ_GENRE, DJ_STATS } from "../data";
import { useMediaQuery, breakpoints } from "../hooks/useMediaQuery";

export default function Hero() {
  const isDesktop = useMediaQuery(breakpoints.lg);

  return (
    <section
      style={{
        display: "flex",
        flexDirection: isDesktop ? "row" : "column",
        minHeight: isDesktop ? "calc(100vh - 64px)" : "auto",
        background: "var(--ink-2)",
        overflow: "hidden",
      }}
    >
      {/* Photo panel */}
      <div
        style={{
          flex: isDesktop ? "0 0 52%" : "none",
          position: "relative",
          background:
            "linear-gradient(160deg, #1c1710 0%, #2e2318 40%, #0d0c0a 100%)",
          overflow: "hidden",
          minHeight: isDesktop ? "auto" : "360px",
        }}
      >
        {/* Artist photo */}
        <img
          src="/images/artist-hero.jpg"
          alt="DJ 30Rack"
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            // anchor to top so faces are never cropped
            objectPosition: "top center",
          }}
        />

        {/* Gradient overlay — darkens bottom for stats readability, subtle top fade */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage: `
              linear-gradient(180deg,
                rgba(13,12,10,0.15) 0%,
                transparent 20%,
                transparent 55%,
                rgba(13,12,10,0.75) 80%,
                rgba(13,12,10,0.92) 100%
              )
            `,
            pointerEvents: "none",
          }}
        />

        {/* Stats — bottom of photo panel */}
        <div
          style={{
            position: "absolute",
            bottom: "24px",
            left: "24px",
            right: "24px",
            display: "flex",
            gap: isDesktop ? "32px" : "24px",
          }}
        >
          {DJ_STATS.map((stat) => (
            <div key={stat.label}>
              <div
                style={{
                  fontFamily: "var(--font-display)",
                  fontWeight: 700,
                  fontSize: isDesktop ? "28px" : "22px",
                  color: "var(--gold)",
                  lineHeight: 1,
                }}
              >
                {stat.value}
              </div>
              <div
                style={{
                  fontFamily: "var(--font-display)",
                  fontSize: "9px",
                  letterSpacing: "2px",
                  textTransform: "uppercase",
                  color: "var(--gold-dim)",
                  marginTop: "4px",
                }}
              >
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Content panel */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: isDesktop ? "flex-end" : "center",
          padding: isDesktop ? "48px 48px 56px 52px" : "36px 24px 40px",
          position: "relative",
          background: "linear-gradient(135deg, #141109 0%, #0d0c0a 100%)",
        }}
      >
        {/* Left border accent — desktop only */}
        {isDesktop && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "1px",
              height: "100%",
              background:
                "linear-gradient(180deg, transparent, rgba(122,101,64,0.4) 50%, transparent)",
            }}
          />
        )}

        {/* Genre eyebrow */}
        <div
          className="animate-fade-in-up"
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "10px",
            letterSpacing: "4px",
            textTransform: "uppercase",
            color: "var(--gold)",
            marginBottom: "16px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <span
            style={{
              display: "block",
              width: "24px",
              height: "1px",
              background: "var(--gold)",
              opacity: 0.6,
            }}
          />
          {DJ_GENRE.join(" · ")}
        </div>

        {/* Name */}
        <div
          className="animate-fade-in-up animation-delay-200"
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 700,
            fontSize: isDesktop ? "clamp(52px, 6vw, 80px)" : "56px",
            lineHeight: 0.95,
            letterSpacing: "-1px",
            textTransform: "uppercase",
            color: "var(--cream)",
            marginBottom: "20px",
          }}
        >
          DJ
          <br />
          <span
            style={{
              color: "var(--gold)",
              fontWeight: 300,
              letterSpacing: isDesktop ? "8px" : "5px",
              fontSize: isDesktop ? "clamp(36px, 4.5vw, 58px)" : "40px",
            }}
          >
            30Rack
          </span>
        </div>

        {/* Tagline */}
        <p
          className="animate-fade-in-up animation-delay-400"
          style={{
            fontFamily: "var(--font-serif)",
            fontStyle: "italic",
            fontSize: "15px",
            lineHeight: 1.8,
            color: "var(--muted)",
            maxWidth: "340px",
            marginBottom: "32px",
          }}
        >
          {DJ_BIO_SHORT}
        </p>

        {/* CTAs */}
        <div
          className="animate-fade-in-up animation-delay-600"
          style={{
            display: "flex",
            flexDirection: isDesktop ? "column" : "row",
            gap: "12px",
            alignItems: isDesktop ? "flex-start" : "center",
            flexWrap: "wrap",
          }}
        >
          <Link to="/contact" className="btn-primary">
            Book Now
          </Link>
          <Link to="/epk" className="btn-ghost">
            <Download size={14} />
            Download EPK
          </Link>
        </div>
      </div>
    </section>
  );
}